import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signOut as firebaseSignOut, type User } from 'firebase/auth';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { handleSSOTokenFromURL } from '../services/ssoService';

interface AuthContextValue {
  firebaseUser: User | null;
  role: string | null;
  loading: boolean;
  isAdmin: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [role, setRole]                 = useState<string | null>(null);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    // 1. Handle SSO token from URL BEFORE processing auth state.
    //    This prevents a race where onAuthStateChanged fires with null
    //    before signInWithCustomToken completes, causing a redirect loop.
    const urlParams = new URLSearchParams(window.location.search);
    const hasSSOToken = urlParams.has('sso_token');
    let ssoSettled = !hasSSOToken; // true if no token to process

    handleSSOTokenFromURL()
      .then((handled) => {
        ssoSettled = true;
        if (handled) console.log('🔑 Account Portal: SSO token processed');
      })
      .catch(() => { ssoSettled = true; });

    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      // Don't process null state while SSO token is still being exchanged
      if (!fbUser && !ssoSettled) {
        return; // signInWithCustomToken will trigger another onAuthStateChanged
      }

      setFirebaseUser(fbUser);

      if (fbUser) {
        try {
          const snap = await getDoc(doc(db, 'users', fbUser.uid));
          if (snap.exists()) {
            const data = snap.data();
            // Effective role priority:
            //   1. appRoles.portal       (modern per-app role)
            //   2. appRoles.okr          (mirror — OKR Tracker superadmin = company admin everywhere)
            //   3. role                  (legacy flat field — backward compat)
            // Same `users/` collection is shared across all Rascal apps, so
            // a user who is superadmin in OKR Tracker is also superadmin here.
            const portalRole = data?.appRoles?.portal as string | undefined;
            const okrRole    = data?.appRoles?.okr    as string | undefined;
            const legacyRole = data?.role             as string | undefined;
            const effectiveRole = portalRole ?? okrRole ?? legacyRole ?? null;
            setRole(effectiveRole);
            // Stamp last sign-in for "Last active" admin telemetry. Best-effort;
            // a failed write here shouldn't block the user from logging in.
            updateDoc(doc(db, 'users', fbUser.uid), { lastSignInAt: serverTimestamp() })
              .catch(err => console.warn('lastSignInAt write failed:', err));
          } else {
            // User exists in Firebase Auth but not in Firestore — unauthorized
            console.warn('Account Portal: no Firestore profile for', fbUser.uid);
            setRole(null);
          }
        } catch (err) {
          console.error('Account Portal: failed to load profile', err);
          setRole(null);
        }
      } else {
        setRole(null);
        // Not authenticated — App.tsx renders the inline LoginPage.
        // (Previously this redirected to a central portal; removed for
        //  standalone Account-Portal dev + simpler ops.)
      }

      setLoading(false);
    });

    return unsub;
  }, []);

  const signOut = async () => {
    await firebaseSignOut(auth);
    setRole(null);
  };

  const isAdmin = role === 'superadmin' || role === 'admin';

  return (
    <AuthContext.Provider value={{ firebaseUser, role, loading, isAdmin, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
